// UltraFlex Admin - Tour Bookings
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, MapPin, Phone, Mail, Clock, User, ChevronDown, ChevronUp, Check, X, UserPlus, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTours = () => {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchTours(); }, []);

  const fetchTours = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads?source=tour_booking&limit=100`, { headers });
      const data = await res.json();
      setLeads(data.items || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ pipeline_stage: status, status: status }),
      });
      fetchTours();
    } catch (err) { console.error(err); }
  };

  const parseNotes = (notes) => {
    if (!notes) return {};
    try { return JSON.parse(notes); } catch { return { message: notes }; }
  };

  const filteredLeads = statusFilter
    ? leads.filter(l => (l.pipeline_stage || l.status) === statusFilter)
    : leads;

  const stageColors = {
    tour_booked: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
    tour_done: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
    joined: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    lost: 'bg-red-500/10 text-red-400 border-red-500/30',
    new: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    contacted: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  };

  const bookedCount = leads.filter(l => (l.pipeline_stage || l.status) === 'tour_booked').length;
  const doneCount = leads.filter(l => (l.pipeline_stage || l.status) === 'tour_done').length;
  const joinedCount = leads.filter(l => (l.pipeline_stage || l.status) === 'joined').length;

  return (
    <div className="p-6 space-y-6" data-testid="admin-tours-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Tour Bookings</h1>
          <p className="text-zinc-400 text-sm mt-1">View and manage gym tour requests</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <p className="text-zinc-500 text-xs mb-1">Total Tours</p>
          <p className="text-2xl font-bold text-white">{leads.length}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <p className="text-zinc-500 text-xs mb-1">Upcoming</p>
          <p className="text-2xl font-bold text-indigo-400">{bookedCount}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <p className="text-zinc-500 text-xs mb-1">Completed</p>
          <p className="text-2xl font-bold text-violet-400">{doneCount}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <p className="text-zinc-500 text-xs mb-1">Converted</p>
          <p className="text-2xl font-bold text-emerald-400">{joinedCount}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'tour_booked', 'tour_done', 'joined', 'lost'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              statusFilter === s
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600'
            }`}
            data-testid={`tour-filter-${s || 'all'}`}
          >
            {s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'All'}
          </button>
        ))}
      </div>

      {/* Tour List */}
      <div className="space-y-3">
        {loading && <div className="text-center py-8 text-zinc-500">Loading...</div>}
        {!loading && filteredLeads.length === 0 && (
          <div className="text-center py-12 text-zinc-500">No tour bookings found.</div>
        )}
        {filteredLeads.map(lead => {
          const info = parseNotes(lead.notes);
          const isExpanded = expandedId === lead.id;
          return (
            <div key={lead.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`tour-card-${lead.id}`}>
              <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : lead.id)}>
                <div className="w-10 h-10 bg-indigo-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold text-sm">{lead.first_name} {lead.last_name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${stageColors[(lead.pipeline_stage || lead.status)] || stageColors.new}`}>
                      {((lead.pipeline_stage || lead.status) || 'new').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-zinc-500 mt-1">
                    {lead.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
                    {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                    {info.preferred_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{info.preferred_date}</span>}
                    {info.preferred_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{info.preferred_time}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {['tour_booked', 'contacted', 'new'].includes(lead.pipeline_stage || lead.status) && (
                    <>
                      {(lead.pipeline_stage || lead.status) !== 'tour_done' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); updateLeadStatus(lead.id, 'tour_done'); }}
                          className="p-1.5 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20"
                          title="Mark as tour done"
                          data-testid={`tour-done-${lead.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {(lead.pipeline_stage || lead.status) !== 'joined' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); updateLeadStatus(lead.id, 'joined'); }}
                          className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20"
                          title="Mark as joined"
                          data-testid={`tour-joined-${lead.id}`}
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); updateLeadStatus(lead.id, 'lost'); }}
                        className="p-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20"
                        title="Mark as no-show/lost"
                        data-testid={`tour-noshow-${lead.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {(lead.pipeline_stage || lead.status) === 'tour_done' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); updateLeadStatus(lead.id, 'joined'); }}
                      className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20"
                      title="Mark as joined"
                      data-testid={`tour-joined-${lead.id}`}
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-zinc-800 p-5 space-y-4 bg-zinc-900/50">
                  {/* Detail Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Preferred Date</p>
                      <p className="text-white text-sm font-medium">{info.preferred_date || 'Not specified'}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Preferred Time</p>
                      <p className="text-white text-sm font-medium">{info.preferred_time || 'Not specified'}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Location</p>
                      <p className="text-white text-sm font-medium">{info.location || lead.site_name || 'Not specified'}</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Created</p>
                      <p className="text-white text-sm font-medium">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}</p>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Contact Details</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-white">{lead.first_name} {lead.last_name}</span>
                      </div>
                      {lead.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-3.5 h-3.5 text-zinc-500" />
                          <a href={`mailto:${lead.email}`} className="text-indigo-400 hover:underline">{lead.email}</a>
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3.5 h-3.5 text-zinc-500" />
                          <a href={`tel:${lead.phone}`} className="text-indigo-400 hover:underline">{lead.phone}</a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  {info.message && (
                    <div className="bg-zinc-800/50 rounded-lg p-4">
                      <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Message from Visitor</p>
                      <p className="text-white text-sm leading-relaxed">{info.message}</p>
                    </div>
                  )}

                  {/* Assignment */}
                  {lead.assigned_to_name && (
                    <div className="flex items-center gap-2 text-xs text-zinc-400 px-1">
                      <User className="w-3 h-3" /> Assigned to: <span className="text-white font-medium">{lead.assigned_to_name}</span>
                    </div>
                  )}

                  {/* Status Actions */}
                  <div className="flex gap-2 pt-2 border-t border-zinc-800">
                    <span className="text-zinc-500 text-xs self-center mr-2">Update status:</span>
                    {['tour_booked', 'tour_done', 'joined', 'lost'].map(s => (
                      <button
                        key={s}
                        onClick={() => updateLeadStatus(lead.id, s)}
                        data-testid={`tour-status-${lead.id}-${s}`}
                        className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                          (lead.pipeline_stage || lead.status) === s
                            ? 'bg-white/10 text-white border-white/20'
                            : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-white'
                        }`}
                      >
                        {s.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminTours;
